import classNames from "classnames"
import React, { useCallback, useMemo } from "react"

import { Analytics } from "@bounce/analytics"
import { Label2, Paragraph2, Title6 } from "@bounce/design-system"
import { formatStripeAmount, useDisplayRegionName, useTranslation } from "@bounce/i18n"
import { isInStorybook } from "@bounce/util"
import type { LinkProps } from "@bounce/web-components"
import { Truncate } from "@bounce/web-components"

import { RichText } from "../../../Generic/RichText/RichText/RichText"
import SafeLink from "../../../Generic/SafeLink/SafeLink"
import { StoreRatingSecondary } from "../StoreRatingSecondary/StoreRatingSecondary"
import { useStoreNickname } from "@/hooks/useStoreNickname"
import { InteractionEvents } from "@/libs/analytics/events"
import { getStoreThumbnail } from "@/libs/bounce/stores/getStoreThumbnail"
import type { TopRatedPoiStore } from "@/libs/bounce/stores/types"
import { useInterpolations } from "@/screens/Generic/StoreScreen/hooks/useInterpolations"

type StoreCardTertiaryProps = Pick<LinkProps, "href"> & {
  as?: React.ElementType
  className?: string
  onClick: (ev: React.MouseEvent<HTMLAnchorElement>, opts?: { section: string }) => void
} & TopRatedPoiStore

const StoreTertiaryCardMaskedImage = ({ href }: { href: string }) => {
  return (
    <div className="relative overflow-hidden pt-[200px]">
      <svg viewBox="0 0 357 350" className="absolute top-0 h-auto w-full">
        <path
          fill="#DEF201"
          d="M271.734 326.162c-81.903-8.303-163.811-16.581-245.7144-24.885-14.5979-1.457-23.7813-15.092-20.5028-30.5L57.5446 26.2633C60.8231 10.8554 74.9243 1.3787 89.0508 5.0515 168.361 25.5404 247.666 46.0545 326.976 66.5433c14.126 3.6729 23.321 17.2578 20.546 30.2992L303.197 305.152c-2.775 13.041-16.865 22.468-31.463 21.01Z"
        />
        <image clipPath="url(#store-card-tertiary-thumbnail-mask)" className="w-full" href={href} />
      </svg>
    </div>
  )
}

export const StoreCardTertiary = ({
  as: Wrapper = "div",
  className,
  customContent,
  href,
  onClick,
  store,
}: StoreCardTertiaryProps) => {
  const { t, i18n } = useTranslation()
  const displayRegionName = useDisplayRegionName()
  const nickname = useStoreNickname({ store })
  const interpolations = useInterpolations({ store, pois: store.pois })

  const thumbnailHref = useMemo(() => {
    if (!!store.thumbnail) {
      return getStoreThumbnail(store, { width: 352, height: 290 })
    }

    if (isInStorybook()) {
      return "https://placehold.co/500x360/png"
    }

    return store.storeCardTertiaryStaticMapUrl
  }, [store])

  const onClickImage = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement>) => onClick(ev, { section: "image" }),
    [onClick],
  )

  const price = formatStripeAmount(store.lowestPrice, store.city.currency, i18n.language)

  return (
    <Wrapper className={classNames(className, "group flex max-w-sm flex-col")}>
      <SafeLink href={href} onClick={onClickImage}>
        <StoreTertiaryCardMaskedImage href={thumbnailHref} />
      </SafeLink>
      <div className="flex h-full flex-col rounded-lg bg-primary-50 p-6 shadow-1">
        <SafeLink href={href} onClick={onClick} className="flex items-start justify-between gap-6">
          <div>
            <Title6 className="line-clamp-2 text-neutral-900">{nickname}</Title6>
            <Paragraph2 className="mt-1 text-neutral-800">
              {store.city.translatedName}, {displayRegionName.of(store.city.country)}
            </Paragraph2>
          </div>
          <StoreRatingSecondary store={store} />
        </SafeLink>
        {!!customContent && (
          <div className="mt-6">
            <Truncate
              as="div"
              className="paragraph2"
              toggleClassName="hidden"
              onClick={(isOpen: boolean) => {
                Analytics.track(InteractionEvents.TruncateToggled, {
                  name: "StoreCardTertiary",
                  open: isOpen,
                })
              }}>
              <RichText
                field={customContent}
                interpolations={interpolations}
                className="[&>*]:paragraph2 [&>*]:text-neutral-800 [&_a]:font-normal [&_a]:text-neutral-800 [&_a]:underline"
              />
            </Truncate>
          </div>
        )}
        <SafeLink href={href} onClick={onClick} className="mt-auto inline-block pt-6">
          <Label2 className="block font-bold text-neutral-800">
            {t("cmp.storeCardTertiary.price", "From {PRICE}/bag/day", { PRICE: price })}
          </Label2>
          <Label2 className="mt-2 block text-primary group-hover:underline">
            {t("cmp.storeCardTertiary.cta", "See storage spot")}
          </Label2>
        </SafeLink>
      </div>
    </Wrapper>
  )
}
